import type { HmacSigner, HmacVerifier } from "@nextday-ai/common-ts-model";

/**
 * Host-supplied dependencies for the attribution shim. Every field has a safe
 * default, so `configureAttribution` is optional during early boot — the shim
 * can still capture unsigned envelopes with a `crypto.randomUUID()` anonymous id
 * until a host override is installed.
 */
export type AttributionHost = {
  signer?: HmacSigner;
  verifier?: HmacVerifier;
  generateAnonymousId: () => string;
  now: () => number;
};

export type AttributionHostOverrides = {
  signer?: HmacSigner;
  verifier?: HmacVerifier;
  generateAnonymousId?: () => string;
  now?: () => number;
};

const defaultGenerateAnonymousId = (): string => {
  if (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }
  return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
};

const defaultHost: AttributionHost = {
  generateAnonymousId: defaultGenerateAnonymousId,
  now: () => Date.now(),
};

let host: AttributionHost = defaultHost;

export const configureAttribution = (overrides: AttributionHostOverrides): void => {
  host = { ...host, ...overrides };
};

export const getAttributionHost = (): AttributionHost => host;

/** Reset host to defaults — exposed for tests. */
export const resetAttributionHost = (): void => {
  host = defaultHost;
};
